import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase } from '@/lib/supabase';
import { getOAuthRedirectUrl, isNativeApp } from '@/lib/nativeApp';

const REFERRAL_STORAGE_KEY = 'isbd_oauth_referral';

export function rememberOAuthReferral(token: string | undefined) {
  const value = token?.trim();
  if (value) sessionStorage.setItem(REFERRAL_STORAGE_KEY, value);
  else sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
}

export function consumeOAuthReferral(): string | null {
  const value = sessionStorage.getItem(REFERRAL_STORAGE_KEY);
  sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
  return value;
}

function callbackPathFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const isCallback =
      parsed.pathname === '/auth/callback' ||
      parsed.pathname.endsWith('/auth/callback') ||
      parsed.searchParams.has('code') ||
      parsed.searchParams.has('token_hash') ||
      parsed.hash.includes('access_token');
    if (!isCallback) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

let nativeOAuthListenerReady = false;

/**
 * Sur APK : le retour OAuth arrive via Custom Tab → intent https://localhost/auth/callback.
 * On ferme le navigateur système et on route le WebView vers le callback (où le verifier PKCE vit).
 */
export function ensureNativeOAuthListener() {
  if (!isNativeApp() || nativeOAuthListenerReady) return;
  nativeOAuthListenerReady = true;

  void CapApp.addListener('appUrlOpen', ({ url }) => {
    const path = callbackPathFromUrl(url);
    if (!path) return;
    void Browser.close().catch(() => undefined);
    if (window.location.pathname === '/auth/callback') {
      const q = window.location.search;
      if (q.includes('code=') || q.includes('token_hash=')) return;
    }
    window.location.assign(path);
  });
}

/** Lance le flux Google (connexion ou création de compte). */
export async function startGoogleAuth(): Promise<{ error: Error | null }> {
  ensureNativeOAuthListener();

  const redirectTo = getOAuthRedirectUrl();
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo,
      skipBrowserRedirect: true,
      queryParams: {
        access_type: 'offline',
        prompt: 'select_account',
      },
    },
  });
  if (error) return { error: new Error(error.message) };
  if (!data?.url) {
    return { error: new Error('Impossible de démarrer la connexion Google.') };
  }

  if (isNativeApp()) {
    try {
      await Browser.open({ url: data.url });
    } catch (err) {
      return {
        error: err instanceof Error ? err : new Error('Impossible d’ouvrir Google.'),
      };
    }
    return { error: null };
  }

  window.location.assign(data.url);
  return { error: null };
}
